/**
 * The amount of data points to display in the chart.
 * The overall rolling window time in seconds is roughly calculated
 * as WINDOW_CHART_DATA_POINTS * CHART_DATA_UPDATE_INTERVAL / 100
 */
export const WINDOW_CHART_DATA_POINTS = 50;

/**
 * This is the interval in milliseconds in which the data points in the data chart is updated
 */
export const CHART_DATA_UPDATE_INTERVAL = 200;

/**
 * The time in milliseconds to interpolate between the last data point and the current time in the chart
 */
export const CHART_INTERPOLATION_TIME = 5 * 1000;

/**
 * The different colors to use for the chart lines
 */
export const GRAPH_COLORS = [
  "#00a8dA",
  "#ffa500",
  "#ff4500",
  "#9400d3",
  "#00ced1",
];

/**
 * Get the color for the graph line based on the index
 */
export const getGraphColorByIndex = (index: number): string =>
  GRAPH_COLORS[index % GRAPH_COLORS.length] ?? "#00a8dA";

/**
 * Represents a data point for the chart.
 */
export interface DataPoint {
  time: Date;
  value: number | null;
}

/**
 * Create an empty data point for the chart as a placeholder when no sensor data is available.
 */
export const createEmptyDataPoint = (): DataPoint => ({
  time: new Date(),
  value: null,
});

/**
 * This function updates the chart data points with the new data point and
 * ensures that only the last WINDOW_CHART_DATA_POINTS points are kept.
 * @param currentDataPoints The current data points
 * @param newDataPoint The new data point to add
 * @returns The updated data points
 */
export const updateDataPoints = (
  currentDataPoints: DataPoint[],
  newDataPoint: DataPoint,
): DataPoint[] => {
  let updatedDataPoints = [...currentDataPoints, newDataPoint];

  // Padding data points with empty data
  while (updatedDataPoints.length < WINDOW_CHART_DATA_POINTS) {
    updatedDataPoints.unshift(createEmptyDataPoint());
  }

  // Ensure only the last WINDOW_CHART_DATA_POINTS points are kept
  if (updatedDataPoints.length > WINDOW_CHART_DATA_POINTS) {
    updatedDataPoints = updatedDataPoints.slice(-WINDOW_CHART_DATA_POINTS);
  }

  return updatedDataPoints;
};
